import { Component, createRef } from 'react';
import styled from 'styled-components';
import { SLINKY, CALYPSO } from 'HubStyleTokens/colors';
import memoizeOne from 'react-utils/memoizeOne';
import AutomationBuilderCard from 'automation-ui-flow-core/canvas/AutomationBuilderCard';
import * as CanvasIdentifiers from 'automation-ui-flow-core/canvas/CanvasIdentifiers';
import { calculateAdjacencyMap, calculateReverseAdjacencyMap } from 'automation-ui-directed-graph/DirectedNodes';
import BezierArrowsSVG from 'automation-ui-directed-graph/BezierArrowsSVG';
import { BUFFER_MARGIN, LEFT_CANVAS_PADDING, NUM_OF_NODES_BEFORE_VIRTUAL_RENDERING, CARD_WIDTH_PADDING, BOTTOM_CANVAS_PADDING } from 'automation-ui-flow-core/canvas/graphConstants';
import SingleConnector from 'automation-ui-directed-graph/SingleConnector';
import BranchConnector from 'automation-ui-directed-graph/BranchConnector';
import { StyledBranchTopSection } from 'automation-ui-directed-graph/ConnectorStyledComponents';
import shouldNodeRender from 'automation-ui-directed-graph/utils/shouldNodeRender';
import calculateNodePositions from 'automation-ui-directed-graph/utils/calculateNodePositions';
import getNodeWrapperWidth from 'automation-ui-directed-graph/utils/getNodeWrapperWidth';
import Rectangle from 'automation-ui-directed-graph/Rectangle';
import BranchTopConnectors from 'automation-ui-directed-graph/BranchTopConnectors';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const StyledGraphContainer = styled.div.withConfig({
  displayName: "DirectedGraph__StyledGraphContainer",
  componentId: "sc-1viyxiy-0"
})(["display:flex;flex-direction:column;align-items:center;min-width:100%;"]);
const StyledGraphNodesContainer = styled.div.withConfig({
  displayName: "DirectedGraph__StyledGraphNodesContainer",
  componentId: "sc-1viyxiy-1"
})(["position:relative;"]);
const StyledSingleNodeContent = styled.div.withConfig({
  displayName: "DirectedGraph__StyledSingleNodeContent",
  componentId: "sc-1viyxiy-2"
})(["display:flex;flex-direction:column;align-items:center;"]);
const StyledTopContentWrapper = styled.div.withConfig({
  displayName: "DirectedGraph__StyledTopContentWrapper",
  componentId: "sc-1viyxiy-3"
})(["margin:0 ", "px;max-width:calc(100vw - ", "px);"], -LEFT_CANVAS_PADDING, CARD_WIDTH_PADDING * 2);
const StyledBranchChildren = styled.div.withConfig({
  displayName: "DirectedGraph__StyledBranchChildren",
  componentId: "sc-1viyxiy-4"
})(["width:100%;display:flex;position:relative;"]);
const StyledSingleNode = styled.div.withConfig({
  displayName: "DirectedGraph__StyledSingleNode",
  componentId: "sc-1viyxiy-5"
})(["display:flex;flex-direction:column;align-items:center;position:absolute;"]);
const StyledBranchNodeChild = styled.div.withConfig({
  displayName: "DirectedGraph__StyledBranchNodeChild",
  componentId: "sc-1viyxiy-6"
})(["padding:0;position:relative;"]);
const StyledFinishLine = styled.div.withConfig({
  displayName: "DirectedGraph__StyledFinishLine",
  componentId: "sc-1viyxiy-7"
})(["width:", "px;position:absolute;display:flex;align-items:center;flex-direction:column;"], ({
  nodeWidth
}) => nodeWidth + 20);
/**
 * By consuming a DirectedNodes Record, this component
 * will rendering any arbitrary graph in the shape
 * of a tree. Note that the graph could be a DAG (and
 * in the future even a DCG, directed cyclic graph)
 * because any non-tree edge is represented as a
 * super imposed arrow referred to as a "go to" edge.
 *
 * The graph implements a 2D virtual renderer via
 * Intersection and Resize Observers. This complex
 * logic allows rendering massive graphs in constant
 * time. However, because of these performance
 * optimizations, it's imperative that this graph only
 * re-render when absolutely necessary.
 *
 * A future optimization could see the graph render
 * as a flat list of nodes rather than nested divs.
 * While this normally wouldn't provide any meaningful
 * improvements to the graph, coupled with a scroll
 * event could allow us to only ever render pieces of
 * the graph that are visible!
 */
class DirectedGraph extends Component {
  constructor(props) {
    super(props);
    this.attachedScrollElement = null;
    this.containerElement = /*#__PURE__*/createRef();
    this.getConnectorLineColor = isHighlighted => {
      const {
        highlightedEdgeColor
      } = this.props;
      if (isHighlighted) {
        return highlightedEdgeColor || CALYPSO;
      }
      return SLINKY;
    };
    this.getConnectorWidth = isHighlighted => {
      return isHighlighted && this.props.highlightedEdgeThickness || 2;
    };
    this.attachScrollEventToCanvas = () => {
      const {
        scrollElementRef
      } = this.props;
      if (!scrollElementRef.current) {
        return;
      }
      scrollElementRef.current.addEventListener('scroll', this.scrollEvent);
      this.resizeObserver.observe(scrollElementRef.current);
      this.attachedScrollElement = scrollElementRef.current;
    };
    this.detachScrollEventToCanvas = () => {
      if (!this.attachedScrollElement) {
        return;
      }
      this.attachedScrollElement.removeEventListener('scroll', this.scrollEvent);
      this.resizeObserver.unobserve(this.attachedScrollElement);
      this.attachedScrollElement = null;
    };
    this.calculateLowestNodeYPosition = memoizeOne(nodePositions => {
      const maxHeight = Array.from(nodePositions.values()).reduce((currentMaxHeight, nextPosition) => {
        return Math.max(currentMaxHeight, nextPosition.outerY);
      }, 0);
      return maxHeight;
    });
    this.scrollEventFrameRequest = NaN;
    this.scrollEvent = evt => {
      const target = evt.target;
      const canvasLeftScroll = target.scrollLeft;
      const canvasTopScroll = target.scrollTop;
      this.setState({
        canvasLeftScroll,
        canvasTopScroll
      });
    };
    this.getApproximateHeightOfGraph = () => {
      const {
        zoomLevel,
        directedGraph,
        nodeHeights,
        showOuterPadding,
        nodeHeight
      } = this.props;
      const nodePositions = calculateNodePositions(directedGraph, nodeHeights);
      const lowestNodeYPosition = this.calculateLowestNodeYPosition(nodePositions);
      const bottomPadding = showOuterPadding ?
      // Add extra scrollable height like a text editor's "scroll past end" feature
      window.innerHeight / 2 :
      // lowestNodeYPosition doesn't account for the end marker
      nodeHeight / 2;
      return Math.round((lowestNodeYPosition + bottomPadding) * zoomLevel);
    };
    this.state = {
      canvasLeftScroll: 0,
      canvasTopScroll: 0,
      scrollElementClientRect: new Rectangle()
    };
    this.resizeObserver = new ResizeObserver(() => {
      const {
        scrollElementRef
      } = this.props;
      const clientRect = scrollElementRef.current ? scrollElementRef.current.getBoundingClientRect() : new Rectangle();
      this.setState({
        scrollElementClientRect: clientRect
      });
    });
  }
  componentDidMount() {
    const {
      scrollElementRef
    } = this.props;
    if (!scrollElementRef.current) {
      return;
    }

    // Type hack to avoid constructing an event and maintaining code parity
    this.scrollEvent({
      target: scrollElementRef.current
    });
    this.attachScrollEventToCanvas();
  }
  componentWillUnmount() {
    this.detachScrollEventToCanvas();
    this._isUnmounted = true;
  }
  componentDidUpdate() {
    const {
      scrollElementRef
    } = this.props;
    if (scrollElementRef.current && scrollElementRef.current !== this.attachedScrollElement) {
      this.attachScrollEventToCanvas();
    }
  }
  getShouldVirtuallyRenderNodes() {
    const {
      isMulti,
      scrollElementRef,
      directedGraph,
      disableVirtualRendering
    } = this.props;
    return !disableVirtualRendering && directedGraph.getNodeIds().length > NUM_OF_NODES_BEFORE_VIRTUAL_RENDERING && !isMulti && !!scrollElementRef && !!scrollElementRef.current;
  }
  getShouldCardContentShow(nodeId) {
    const {
      directedGraph,
      nodeHeights,
      nodeHeight
    } = this.props;
    const {
      canvasLeftScroll,
      canvasTopScroll,
      scrollElementClientRect
    } = this.state;
    const zoomLevel = this.getZoomLevel();
    if (!this.getShouldVirtuallyRenderNodes() || nodeId === CanvasIdentifiers.FIRST_NODE) {
      return true;
    }
    const nodeInScreenViewport = shouldNodeRender({
      nodeId,
      bufferMargin: BUFFER_MARGIN / 2,
      nodeHeights,
      nodeHeight,
      zoomLevel: zoomLevel,
      boundingClientRectDimensions: scrollElementClientRect,
      canvasScrollPositions: {
        canvasLeftScroll,
        canvasTopScroll
      },
      directedGraph
    });
    return nodeInScreenViewport;
  }
  getFollowingEdges(nodeId) {
    const {
      directedGraph
    } = this.props;
    const adjacencyMap = calculateAdjacencyMap(directedGraph.getEdges());
    return adjacencyMap.get(nodeId) || [];
  }
  renderFinishLine({
    edge
  }) {
    const {
      onRenderEndMarker
    } = this.props;
    return onRenderEndMarker(edge.getId());
  }
  renderBranchFinishLine({
    edge
  }) {
    const {
      onRenderEndMarker,
      nodeWidth
    } = this.props;
    return /*#__PURE__*/_jsx(StyledFinishLine, {
      className: CanvasIdentifiers.DIRECTED_GRAPH_FINISH_LINE,
      nodeWidth: nodeWidth,
      children: onRenderEndMarker(edge.getId())
    });
  }
  renderPlaceholderCard() {
    return /*#__PURE__*/_jsx(AutomationBuilderCard, {
      isSelected: false,
      isHighlighted: false,
      titleUseTheme: "default",
      isDisabled: false,
      lazyLoad: true
    });
  }
  getZoomLevel() {
    const {
      zoomLevel,
      directedGraph
    } = this.props;
    const startNodeId = directedGraph.getStartingNodeId();
    const canvasWidth = getNodeWrapperWidth(startNodeId, directedGraph);
    return this.containerElement.current ? this.containerElement.current.getBoundingClientRect().width / canvasWidth : zoomLevel;
  }
  renderSingleNode({
    nodeId
  }) {
    const {
      directedGraph,
      onRenderNode,
      nodeWidth,
      nodeHeight,
      nodeHeights
    } = this.props;
    const {
      canvasLeftScroll,
      canvasTopScroll,
      scrollElementClientRect
    } = this.state;
    const zoomLevel = this.getZoomLevel();
    const nodeInBufferViewport = !this.getShouldVirtuallyRenderNodes() || shouldNodeRender({
      nodeId,
      nodeHeights,
      nodeHeight,
      zoomLevel,
      boundingClientRectDimensions: scrollElementClientRect,
      canvasScrollPositions: {
        canvasLeftScroll,
        canvasTopScroll
      },
      directedGraph
    });
    if (!nodeInBufferViewport) {
      return null;
    }
    const followingEdges = this.getFollowingEdges(nodeId);
    const nodeWrapperWidth = getNodeWrapperWidth(nodeId, directedGraph);
    const nodePositions = calculateNodePositions(directedGraph, nodeHeights);
    const estimatedPosition = nodePositions.get(nodeId);
    const shouldContentShow = this.getShouldCardContentShow(nodeId);
    const nodeContentId = `node-${nodeId}`;
    return /*#__PURE__*/_jsxs(StyledSingleNode, {
      className: CanvasIdentifiers.SINGLE_NODE,
      id: `node-${nodeId}-wrapper`,
      style: {
        width: nodeWrapperWidth ? `${nodeWrapperWidth}px` : '',
        left: `${estimatedPosition === null || estimatedPosition === void 0 ? void 0 : estimatedPosition.outerX}px`,
        top: `${estimatedPosition === null || estimatedPosition === void 0 ? void 0 : estimatedPosition.y}px`
      },
      children: [/*#__PURE__*/_jsx(StyledSingleNodeContent, {
        id: nodeContentId,
        className: "node-card",
        children: shouldContentShow ? onRenderNode({
          id: nodeId,
          width: nodeWidth,
          height: nodeHeights.get(nodeContentId) || nodeHeight
        }) : this.renderPlaceholderCard()
      }, nodeId), this.renderConnectors({
        nodeId,
        edges: followingEdges
      })]
    }, `single-node-${nodeId}`);
  }
  renderBranchNodeAndConnectors({
    nodeId,
    edges
  }) {
    var _nodePositions$get;
    const {
      directedGraph,
      highlightedNodeIds,
      onRenderBranchName,
      nodeWidth,
      getIsBranchEdgeHighlighted,
      nodeHeights
    } = this.props;
    const nodePositions = calculateNodePositions(directedGraph, nodeHeights);
    const meetingPointSurroundingBranches = {
      previousBranchIndex: -1,
      nextBranchIndex: -1
    };
    const branchNodeWrapperWidth = getNodeWrapperWidth(nodeId, directedGraph);
    const branchNodePosition = ((_nodePositions$get = nodePositions.get(nodeId)) === null || _nodePositions$get === void 0 ? void 0 : _nodePositions$get.x) || 0;
    // starting position of the first branch node
    let currentBranchXPos = branchNodePosition - branchNodeWrapperWidth / 2;
    const branches = edges.map((edge, index) => {
      const nextActionId = edge.getToNode();
      const prevActionId = edge.getFromNode();
      const nodeWrapperWidth = getNodeWrapperWidth(nextActionId, directedGraph);
      let isHighlighted = highlightedNodeIds && highlightedNodeIds.has(prevActionId) && highlightedNodeIds.has(nextActionId);
      let distanceFromMeetingPoint = 0;
      if (prevActionId !== null) {
        var _nodePositions$get2;
        const fromNodeXPos = ((_nodePositions$get2 = nodePositions.get(prevActionId)) === null || _nodePositions$get2 === void 0 ? void 0 : _nodePositions$get2.x) || 0;
        // Using the same logic done for empty branches and intentionally not using the next action node positions
        const toNodeXPos = currentBranchXPos + nodeWrapperWidth / 2;
        distanceFromMeetingPoint = toNodeXPos - fromNodeXPos;
      }
      // update the current branch x position for the next branch node
      currentBranchXPos += nodeWrapperWidth;

      // This is needed to conditionally highlight only relevant branch segments
      if (meetingPointSurroundingBranches.previousBranchIndex === -1 && meetingPointSurroundingBranches.nextBranchIndex === -1) {
        // This object gets updated only once for every branch action
        if (distanceFromMeetingPoint > 0) {
          meetingPointSurroundingBranches.previousBranchIndex = index - 1;
          meetingPointSurroundingBranches.nextBranchIndex = index;
        } else if (distanceFromMeetingPoint === 0) {
          meetingPointSurroundingBranches.previousBranchIndex = index;
          meetingPointSurroundingBranches.nextBranchIndex = index;
        }
      }
      const currentEdgeId = edge.getId();
      if (currentEdgeId !== null && getIsBranchEdgeHighlighted) {
        isHighlighted = getIsBranchEdgeHighlighted(currentEdgeId);
      }
      return /*#__PURE__*/_jsxs(StyledBranchNodeChild, {
        className: CanvasIdentifiers.BRANCH_NODE_CHILD,
        style: {
          width: nodeWrapperWidth ? `${nodeWrapperWidth}px` : undefined
        },
        children: [/*#__PURE__*/_jsx(BranchTopConnectors, {
          currentBranchIndex: index,
          distanceFromMeetingPoint: distanceFromMeetingPoint,
          nodeWrapperWidth: nodeWrapperWidth,
          highlightedNodeIds: highlightedNodeIds,
          edges: edges,
          meetingPointSurroundingBranches: meetingPointSurroundingBranches,
          getIsBranchEdgeHighlighted: getIsBranchEdgeHighlighted,
          getConnectorLineColor: this.getConnectorLineColor,
          getConnectorWidth: this.getConnectorWidth
        }), /*#__PURE__*/_jsx(BranchConnector, {
          edge: edge,
          color: this.getConnectorLineColor(isHighlighted),
          edgeThickness: this.getConnectorWidth(isHighlighted),
          onRenderBranchName: onRenderBranchName,
          children: this.renderConnectorButton({
            edge,
            isHighlighted
          })
        }), !nextActionId && this.renderBranchFinishLine({
          edge
        })]
      }, index);
    });
    const isHighlighted = highlightedNodeIds && highlightedNodeIds.has(nodeId);
    return /*#__PURE__*/_jsxs(StyledBranchChildren, {
      className: CanvasIdentifiers.BRANCH_NODES,
      children: [/*#__PURE__*/_jsx(StyledBranchTopSection, {
        align: "right",
        className: "p-y-9",
        color: this.getConnectorLineColor(isHighlighted),
        edgeThickness: this.getConnectorWidth(isHighlighted),
        nodeWidth: nodeWidth
      }), /*#__PURE__*/_jsx(StyledBranchTopSection, {
        align: "left",
        className: "p-y-9",
        color: this.getConnectorLineColor(isHighlighted),
        edgeThickness: this.getConnectorWidth(isHighlighted),
        nodeWidth: nodeWidth
      }), branches]
    });
  }

  /**
    This is needed to highlight goto edge for performance history and workflow testing features.
    if the goto action is a direct child of a branch node, we rely on the getIsBranchEdgeHighlighted callback thats used to highlight branch edges.
    if not we rely on the highlightedNodeIds to highlight the goto edge.
   */
  getHighlightedGotoPaths() {
    const {
      directedGraph,
      highlightedNodeIds,
      getIsBranchEdgeHighlighted
    } = this.props;
    const gotoPaths = directedGraph.getGoToPaths();
    const {
      reverseAdjacencyMap
    } = calculateReverseAdjacencyMap(directedGraph.getEdges(), true);
    return new Set(Object.keys(directedGraph.getGoToPaths()).filter(pathId => {
      const prevEdge = reverseAdjacencyMap.get(pathId)[0];
      const previousNodeId = prevEdge.getFromNode();
      // This should always be a non null value because we cannot have a goto action to an end marker.
      const nextNodeId = gotoPaths[pathId].toNodeId;
      const isDirectChildToBranchNode = this.getFollowingEdges(previousNodeId).length > 1;
      return isDirectChildToBranchNode && (getIsBranchEdgeHighlighted === null || getIsBranchEdgeHighlighted === void 0 ? void 0 : getIsBranchEdgeHighlighted(prevEdge.getId())) || !isDirectChildToBranchNode && (highlightedNodeIds === null || highlightedNodeIds === void 0 ? void 0 : highlightedNodeIds.has(previousNodeId)) && highlightedNodeIds.has(nextNodeId);
    }));
  }
  renderGoToLines() {
    const {
      directedGraph,
      nodeWidth,
      nodeHeights,
      goToLineStrokeWidth
    } = this.props;
    const {
      canvasLeftScroll,
      canvasTopScroll,
      scrollElementClientRect
    } = this.state;
    return /*#__PURE__*/_jsx(BezierArrowsSVG, {
      goToPaths: directedGraph.get('goToPaths'),
      nodeHeights: nodeHeights,
      directedGraph: directedGraph,
      canvasLeftScroll: canvasLeftScroll,
      canvasTopScroll: canvasTopScroll,
      zoomLevel: this.getZoomLevel(),
      scrollElementClientRect: scrollElementClientRect,
      shouldVirtuallyRenderArrows: this.getShouldVirtuallyRenderNodes(),
      nodeWidth: nodeWidth,
      lineStrokeWidth: goToLineStrokeWidth,
      lineColor: SLINKY,
      highlightedGotoPaths: this.getHighlightedGotoPaths()
    });
  }
  renderConnectorButton({
    edge,
    isHighlighted
  }) {
    const {
      graphId,
      isMulti,
      readOnly,
      showEdgeTooltips,
      onRenderConnectorButton
    } = this.props;
    return onRenderConnectorButton && /*#__PURE__*/_jsx("div", {
      className: CanvasIdentifiers.ADD_BUTTON_CONTAINER,
      children: onRenderConnectorButton({
        edge,
        graphId,
        isMulti,
        isHighlighted,
        showEdgeTooltips,
        readOnly
      })
    });
  }
  renderConnectors({
    nodeId,
    edges
  }) {
    const {
      highlightedNodeIds,
      directedGraph
    } = this.props;
    if (!edges.length) {
      return null;
    }
    if (edges.length === 1) {
      const [edge] = edges;
      const nextActionId = edge.getToNode();
      const isNextGotoAction = !!nextActionId && nextActionId in directedGraph.goToPaths;
      const isHighlighted = highlightedNodeIds && highlightedNodeIds.has(edge.getFromNode()) && (!nextActionId || highlightedNodeIds.has(nextActionId) || isNextGotoAction);
      return /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(SingleConnector, {
          color: this.getConnectorLineColor(isHighlighted),
          edgeThickness: this.getConnectorWidth(isHighlighted),
          children: this.renderConnectorButton({
            edge,
            isHighlighted
          })
        }), !nextActionId && this.renderFinishLine({
          edge
        })]
      });
    }
    return this.renderBranchNodeAndConnectors({
      nodeId,
      edges
    });
  }
  renderNodes() {
    const {
      directedGraph
    } = this.props;
    return directedGraph.getNodeIds().concat(Object.keys(directedGraph.getGoToPaths())).map(nodeId => {
      return this.renderSingleNode({
        nodeId
      });
    });
  }
  render() {
    const {
      directedGraph,
      isEmbedded,
      TopContent,
      showOuterPadding,
      setFirstNodeRef
    } = this.props;
    const startNodeId = directedGraph.getStartingNodeId();
    const width = getNodeWrapperWidth(startNodeId, directedGraph);
    const height = this.getApproximateHeightOfGraph();
    const canvasWidth = width + (showOuterPadding ? LEFT_CANVAS_PADDING * 2 : 0);
    const canvasHeight = height + (showOuterPadding ? BOTTOM_CANVAS_PADDING : 0);
    const renderedTopContent = isEmbedded ? TopContent : /*#__PURE__*/_jsx(StyledTopContentWrapper, {
      children: TopContent
    });
    return /*#__PURE__*/_jsxs(StyledGraphContainer, {
      showOuterPadding: showOuterPadding,
      ref: setFirstNodeRef,
      style: {
        width: `${canvasWidth}px`
      },
      children: [renderedTopContent, /*#__PURE__*/_jsxs(StyledGraphNodesContainer, {
        ref: this.containerElement,
        style: {
          width: `${width}px`,
          height: `${canvasHeight}px`
        },
        id: CanvasIdentifiers.STARTING_NODE_ID,
        children: [this.renderGoToLines(), this.renderNodes()]
      })]
    });
  }
}
DirectedGraph.defaultProps = {
  disableVirtualRendering: false,
  isMulti: false,
  scrollElementRef: {}
};
export default DirectedGraph;